import {Button, Dropdown, Form, Input, TextArea} from "semantic-ui-react";
import {IItem} from "../../interfaces/Item";
import {rotationAngles, round} from "../../utils/constants";
import {IBarcode} from "../../interfaces/Barcode";
import {C128, DATAMATRIX, DATAMATRIX_GS1, QR, barcodeTypes, dataMatrixSizes, getBarcodeDefaultValue} from "../../utils/barcodeUtils";
import {useAppDispatch} from "../../app/hooks";
import {ChangeBarcodeValue, GenerateBarcode} from "../BarcodeActions";
import {RootState} from "../../app/store";
import {useSelector} from "react-redux";
import {ITag} from "../../interfaces/Tag";
import {useTranslation} from "react-i18next";

interface Props {
  style: any;
  selectedItem: IItem;
  pxInMM: number;
  onChangeWithStyle: (propName: string, propValue: any, propNameStyle: string, propValueStyle: any) => void;
  onChange: (propName: string, propValue: any) => void;
  onChangeNoHistory: (propName: string, propValue: any) => void;
  handleAddNewRecord?: () => void;
  role: string;
}

export const BarcodeForm = ({style, selectedItem, pxInMM, onChangeWithStyle, onChange, onChangeNoHistory, handleAddNewRecord, role}: Props) => {
  const dispatch = useAppDispatch();
  const {t} = useTranslation();
  const {barcodeTags} = useSelector((state: RootState) => state.refData);
  const barcodeTagListDD = barcodeTags.map((tag: ITag) => {
    return {
      key: tag.id,
      text: tag.name,
      value: tag.id,
    };
  });
  const onBarcodeChange = (barcodeName: string, propName: string, propValue: any) => {
    const isSquareBarcode = selectedItem?.barcodeType === DATAMATRIX || selectedItem?.barcodeType === DATAMATRIX_GS1 || selectedItem?.barcodeType === QR;
    let barcode: IBarcode = {
      X: selectedItem?.xInMM || 0,
      Y: selectedItem?.yInMM || 0,
      Name: selectedItem?.name || "",
      Width: selectedItem?.width || 0,
      Height: isSquareBarcode ? selectedItem?.width || 0 : selectedItem?.height || 0,
      ShowCaption: Number(selectedItem?.showCaption) || 0,
      CaptionSize: selectedItem?.captionSize || 0,
      Rotation: selectedItem?.rotation || 0,
      Type: selectedItem?.barcodeType || C128,
      DefaultText: barcodeName === "Name" ? getBarcodeDefaultValue(barcodeTags, propValue) : selectedItem?.defaultText || "",
      DataMatrixSize: selectedItem?.dataMatrixSize || "Auto",
      [barcodeName]: propValue,
    };
    dispatch(GenerateBarcode(selectedItem.itemid, barcode)).then((barcodeUrl) => {
      if (barcodeUrl && selectedItem !== undefined && selectedItem !== null) {
        dispatch(ChangeBarcodeValue(selectedItem, propName, propValue, barcodeUrl));
      }
    });
  };
  return (
    <Form style={style.form}>
      <Form.Field>
        <label style={{textAlign: "left"}}>{t("variable_name")}:</label>
        <div style={{display: "flex", alignItems: "start", width: "100%"}}>
          <Dropdown
            size="small"
            options={barcodeTagListDD}
            value={selectedItem.name}
            fluid
            selection={true}
            search={(options, query) =>
              options.filter((option) => option.text?.toString().toLowerCase().includes(query.toLowerCase()) || option.value?.toString().toLowerCase().includes(query.toLowerCase()))
            }
            onChange={(e, data) => {
              onChange("defaultText", getBarcodeDefaultValue(barcodeTags, data.value));
              onBarcodeChange("Name", "name", data.value);
            }}
          />
          {(role === "admin" || role === "super_admin") && <Button onClick={handleAddNewRecord} className="icon" icon="plus" basic color="blue" style={{marginLeft: 4}} />}
        </div>
      </Form.Field>
      <Form.Field>
        <label style={{textAlign: "left"}}>{t("default_text")}:</label>
        <TextArea
          rows={1}
          value={selectedItem.defaultText}
          placeholder={t("enter_default_value_here")}
          onChange={(e) => {
            onChangeNoHistory("defaultText", e.target.value);
          }}
          onKeyDown={(e: any) => {
            if (e.key === "Enter") {
              onBarcodeChange("DefaultText", "defaultText", e.target.value);
            }
          }}
          onBlur={(e: any) => {
            e.preventDefault();
            onBarcodeChange("DefaultText", "defaultText", e.target.value);
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label>{t("x_from")}:</label>
        <Input
          fluid
          size="small"
          style={{width: "100%"}}
          type="number"
          value={round(selectedItem.xInMM)}
          onChange={(e) => {
            onChangeWithStyle("xInMM", Number(e.target.value), "left", `${Number(e.target.value) * pxInMM}px`);
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label>{t("y_from")}:</label>
        <Input
          fluid
          size="small"
          style={{width: "100%"}}
          type="number"
          value={round(selectedItem.yInMM)}
          onChange={(e) => {
            onChangeWithStyle("yInMM", Number(e.target.value), "top", `${Number(e.target.value) * pxInMM}px`);
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label>{t("width")}:</label>
        <Input
          fluid
          size="small"
          style={{width: "100%"}}
          type="number"
          value={round(selectedItem.width)}
          onChange={(e) => {
            onChangeNoHistory("width", Number(e.target.value));
            if (selectedItem.barcodeType === DATAMATRIX || selectedItem.barcodeType === DATAMATRIX_GS1 || selectedItem.barcodeType === QR) {
              onChangeNoHistory("height", Number(e.target.value));
            }
          }}
          onKeyDown={(e: any) => {
            if (e.key === "Enter") {
              onBarcodeChange("Width", "width", Number(e.target.value));
            }
          }}
          onBlur={(e: any) => {
            e.preventDefault();
            onBarcodeChange("Width", "width", Number(e.target.value));
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label>{t("height")}:</label>
        <Input
          fluid
          size="small"
          style={{width: "100%"}}
          type="number"
          value={round(selectedItem.height)}
          onChange={(e) => {
            onChangeNoHistory("height", Number(e.target.value));
            if (selectedItem.barcodeType === DATAMATRIX || selectedItem.barcodeType === DATAMATRIX_GS1 || selectedItem.barcodeType === QR) {
              onChangeNoHistory("width", Number(e.target.value));
            }
          }}
          onKeyDown={(e: any) => {
            if (e.key === "Enter") {
              onBarcodeChange("Height", "height", Number(e.target.value));
            }
          }}
          onBlur={(e: any) => {
            e.preventDefault();
            onBarcodeChange("Height", "height", Number(e.target.value));
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label>{t("rotation")}:</label>
        <Dropdown
          size="small"
          options={rotationAngles}
          value={Number(selectedItem.rotation)}
          fluid
          selection
          search={(options, query) =>
            options.filter((option) => option.text?.toString().toLowerCase().includes(query.toLowerCase()) || option.value?.toString().toLowerCase().includes(query.toLowerCase()))
          }
          onChange={(e, data) => {
            onBarcodeChange("Rotation", "rotation", Number(data.value));
          }}
        />
      </Form.Field>

      <Form.Field inline style={style.field}>
        <label>{t("caption_size")}:</label>
        <Input
          fluid
          size="small"
          style={{width: "100%"}}
          type="number"
          value={selectedItem.captionSize}
          onChange={(e) => {
            onChangeNoHistory("captionSize", Number(e.target.value));
          }}
          onKeyDown={(e: any) => {
            if (e.key === "Enter") {
              onBarcodeChange("CaptionSize", "captionSize", Number(e.target.value));
            }
          }}
          onBlur={(e: any) => {
            e.preventDefault();
            onBarcodeChange("CaptionSize", "captionSize", Number(e.target.value));
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label>{t("show_caption")}:</label>
        <Form.Checkbox
          checked={selectedItem.showCaption ? true : false}
          onChange={(e, data) => {
            onBarcodeChange("ShowCaption", "showCaption", data.checked ? 1 : 0);
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label>{t("barcode_type")}:</label>
        <Dropdown
          size="small"
          options={barcodeTypes}
          value={selectedItem.barcodeType}
          fluid
          selection={true}
          search={(options, query) =>
            options.filter((option) => option.text?.toString().toLowerCase().includes(query.toLowerCase()) || option.value?.toString().toLowerCase().includes(query.toLowerCase()))
          }
          onChange={(e, data) => {
            onBarcodeChange("Type", "barcodeType", data.value);
            if (data.value === DATAMATRIX || data.value === DATAMATRIX_GS1 || data.value === QR) {
              onChangeNoHistory("height", selectedItem.width);
            }
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label>{t("data_matrix_size")}:</label>
        <Dropdown
          size="small"
          options={dataMatrixSizes}
          value={selectedItem.dataMatrixSize}
          fluid
          selection={true}
          search={(options, query) =>
            options.filter((option) => option.text?.toString().toLowerCase().includes(query.toLowerCase()) || option.value?.toString().toLowerCase().includes(query.toLowerCase()))
          }
          disabled={selectedItem.barcodeType !== DATAMATRIX && selectedItem.barcodeType !== DATAMATRIX_GS1}
          onChange={(e, data) => {
            onBarcodeChange("DataMatrixSize", "dataMatrixSize", data.value);
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        {/*<label>{t("bring_to_back")}:</label>
         <Form.Checkbox
          checked={selectedItem.bringBack === 1 ? true : false}
          onChange={(e, data) => {
            onChange("bringBack", selectedItem.bringBack === 1 ? 2 : 1);
          }}
        /> */}
        <Button
          compact fluid basic
          onClick={() => {
            onChange("bringBack", selectedItem.bringBack === 1 ? 2 : 1);
          }}
        >
          {t("bring_to_back")}
        </Button>
      </Form.Field>
    </Form>
  );
};
